import { useEffect, useRef, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { getUserProfile } from '../../api/users'
import { getUnreadCount } from '../../api/notifications'
import { useAuthStore } from '../../store/auth'
import { useUnsavedChangesBlocker } from '../../hooks/useUnsavedChangesGuard'
import { Logo } from '../Logo'
import { ErrorBoundary } from '../ErrorBoundary'
import { PageHeaderContext, usePageHeaderProvider } from './PageHeaderContext'

const POLL_INTERVAL_MS = 30_000
const MOBILE_BP = 768
const HEADER_HEIGHT = 54
const HATCH_BG = 'repeating-linear-gradient(45deg, transparent, transparent 5px, color-mix(in srgb, var(--color-fg) 7%, transparent) 5px, color-mix(in srgb, var(--color-fg) 7%, transparent) 6px)'

function NavIcon({ d }: { d: string }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d={d} />
    </svg>
  )
}

const NAV_ICONS = {
  journal: 'M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z',
  todos: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
  feed: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z',
  tags: 'M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z',
  media: 'M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4 2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z',
  admin: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z',
  settings: 'M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.56-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z',
  logout: 'M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z',
} as const

const PAGE_ERROR_FALLBACK = (
  <div style={{ padding: '2rem' }}>
    <p style={{ color: 'var(--color-1)', marginBottom: '1rem' }}>this page encountered an error.</p>
    <a href="/">go home</a>
  </div>
)

export function Shell() {
  useUnsavedChangesBlocker()
  const { username, clearAuth, setEmailVerified, setIsSudo, isSudo } = useAuthStore()
  const [unreadCount, setUnreadCount] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BP)
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const pageHeader = usePageHeaderProvider()

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BP - 1}px)`)
    const handle = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches)
      if (!e.matches) setMenuOpen(false)
    }
    mq.addEventListener('change', handle)
    return () => mq.removeEventListener('change', handle)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!username) return
    getUserProfile(username)
      .then((p) => {
        if (typeof p.email_verified === 'boolean') setEmailVerified(p.email_verified)
        if (typeof p.is_sudo === 'boolean') setIsSudo(p.is_sudo)
      })
      .catch(() => {})
  }, [username]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!username) return

    function poll() {
      getUnreadCount()
        .then((r) => setUnreadCount(r.count))
        .catch(() => {})
    }

    poll()
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [username])

  const closeMenu = () => setMenuOpen(false)

  const notifButton = username ? (
    <Link
      to="/notifications"
      onClick={() => setUnreadCount(0)}
      aria-label={`notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      style={{ position: 'relative', display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit', flexShrink: 0, padding: '2px' }}
    >
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 2a1 1 0 0 1 1 1v.35A5 5 0 0 1 14 8v3l1.5 2.5h-13L4 11V8A5 5 0 0 1 8 3.35V3a1 1 0 0 1 1-1z" />
        <path d="M7 13.5a2 2 0 0 0 4 0" />
      </svg>
      {unreadCount > 0 && (
        <span style={{
          position: 'absolute',
          top: -2,
          right: -4,
          background: 'var(--color-1)',
          color: 'var(--color-bg)',
          borderRadius: '999px',
          fontSize: '0.6rem',
          lineHeight: 1,
          padding: '0.15em 0.35em',
          minWidth: '1.3em',
          textAlign: 'center',
        }}>
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </Link>
  ) : null

  const nl = { display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.4rem' } as const
  const navLinks = username ? (
    <>
      <Link to={`/~${username}/journal`} onClick={closeMenu} style={nl}><NavIcon d={NAV_ICONS.journal} />journal</Link>
      <Link to={`/~${username}/todos`} onClick={closeMenu} style={nl}><NavIcon d={NAV_ICONS.todos} />todos</Link>
      <Link to="/feed" onClick={closeMenu} style={nl}><NavIcon d={NAV_ICONS.feed} />feed</Link>
      <Link to={`/~${username}/tags`} onClick={closeMenu} style={nl}><NavIcon d={NAV_ICONS.tags} />tags</Link>
      <Link to={`/~${username}/media`} onClick={closeMenu} style={nl}><NavIcon d={NAV_ICONS.media} />media</Link>
      {isSudo && <Link to="/admin" onClick={closeMenu} style={nl}><NavIcon d={NAV_ICONS.admin} />admin</Link>}
      <Link to="/settings" onClick={closeMenu} style={{ ...nl, marginBottom: '1.5rem' }}><NavIcon d={NAV_ICONS.settings} />settings</Link>
      <button type="button" onClick={clearAuth} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', width: '100%', fontSize: '0.8rem' }}>
        <NavIcon d={NAV_ICONS.logout} />logout
      </button>
    </>
  ) : (
    <Link to="/login" onClick={closeMenu}>login</Link>
  )

  const headerContent = (
    <>
      {pageHeader.title && (
        <span style={{ fontSize: '1.1rem', fontWeight: 'bold', marginRight: '1rem', flexShrink: 0 }}>
          {pageHeader.title}
        </span>
      )}
      {pageHeader.actions && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          {pageHeader.actions}
        </div>
      )}
      {!pageHeader.actions && <div style={{ flex: 1 }} />}
      {notifButton}
    </>
  )

  if (isMobile) {
    return (
      <PageHeaderContext.Provider value={pageHeader}>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <header style={{
            position: 'sticky',
            top: 0,
            zIndex: 50,
            background: 'var(--color-bg)',
            backgroundImage: HATCH_BG,
            borderBottom: '1px solid var(--color-fg)',
            padding: '0 1rem',
            height: HEADER_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}>
            <button
              type="button"
              onClick={() => setMenuOpen(o => !o)}
              aria-label={menuOpen ? 'close menu' : 'open menu'}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {menuOpen ? (
                <span style={{ color: 'var(--color-fg)', fontSize: '1.25rem', lineHeight: 1 }}>✕</span>
              ) : (
                <Logo size={28} />
              )}
            </button>
            {headerContent}
          </header>

          {menuOpen && (
            <div
              onClick={closeMenu}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 90,
              }}
            />
          )}

          <nav style={{
            position: 'fixed',
            top: 0,
            left: menuOpen ? 0 : -200,
            width: 180,
            height: '100vh',
            borderRight: '1px solid var(--color-fg)',
            padding: '1rem',
            background: 'var(--color-bg)',
            backgroundImage: HATCH_BG,
            zIndex: 100,
            transition: 'left 0.2s ease',
            overflowY: 'auto',
          }}>
            <Link
              to="/"
              onClick={closeMenu}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.25rem',
                marginBottom: '2rem',
                marginTop: '0.5rem',
                textDecoration: 'none',
              }}
            >
              <Logo size={48} />
            </Link>
            {navLinks}
          </nav>

          <main id="page-content" style={{ flex: 1, padding: '1rem', width: '100%' }}>
            <ErrorBoundary fallback={PAGE_ERROR_FALLBACK}><Outlet /></ErrorBoundary>
          </main>
        </div>
      </PageHeaderContext.Provider>
    )
  }

  return (
    <PageHeaderContext.Provider value={pageHeader}>
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        <nav style={{ width: 180, padding: '1rem', flexShrink: 0, position: 'sticky', top: 0, height: '100vh', overflowY: 'auto', backgroundImage: HATCH_BG }}>
          <Link
            to="/"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.25rem',
              marginBottom: '0.75rem',
              textDecoration: 'none',
            }}
          >
            <Logo size={88} />
          </Link>
          {navLinks}
        </nav>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <header style={{
            height: HEADER_HEIGHT,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            zIndex: 50,
            background: 'var(--color-bg)',
            backgroundImage: HATCH_BG,
            borderBottom: '1px solid var(--color-fg)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 1rem',
            gap: '0.5rem',
          }}>
            {headerContent}
          </header>
          <main id="page-content" style={{ flex: 1, padding: '2rem', maxWidth: 760, borderLeft: '1px solid var(--color-fg)' }}>
            <ErrorBoundary fallback={PAGE_ERROR_FALLBACK}><Outlet /></ErrorBoundary>
          </main>
        </div>
      </div>
    </PageHeaderContext.Provider>
  )
}
